import { Component, OnDestroy, OnInit } from '@angular/core';
import { AcModel } from 'projects/runner/src/app/models/ac.model';
import { RunnerOnlinetestModel } from 'projects/runner/src/app/models/runner-onlinetest.model';
import {
  faArrowRight,
  faCheck,
  faHourglassHalf,
  faQuestionCircle
} from '@fortawesome/pro-light-svg-icons';
import { Router } from '@angular/router';
import { ApiService } from 'projects/runner/src/app/services/api.service';
import { UiService } from 'projects/runner/src/app/services/ui.service';
import { TranslocoService } from '@ngneat/transloco';
import { AuthStateService } from 'projects/runner/src/app/services/auth-state.service';
import { CandidateAcOnlinetestModel } from 'projects/core/src/lib/models/candidate-ac-onlinetest.model';
import { Subscription } from 'rxjs';
import { DataService } from 'projects/runner/src/app/services/data.service';

@Component({
  selector: 'app-onlinetest-container',
  templateUrl: './onlinetest-container.component.html',
  styleUrls: ['./onlinetest-container.component.scss']
})
export class OnlinetestContainerComponent implements OnInit, OnDestroy {

  public onlinetests: Array<RunnerOnlinetestModel> = [];
  public loading = false;
  public isMod = this.auth.isModerator();
  public isCandidate = this.auth.isCandidate();
  public isObserver = this.auth.isObserver();
  public isOpen = false;
  public showForObservers = false;

  public ac: AcModel = null;
  public acSubscription: Subscription = null;

  public faArrowRight = faArrowRight;
  public faHourglassHalf = faHourglassHalf;
  public faCheck = faCheck;
  public faExplanation = faQuestionCircle;

  public explainedOnlinetest: RunnerOnlinetestModel = null;
  public explainedOnlinetestVisible = false;

  constructor(
    private data: DataService,
    private router: Router,
    public auth: AuthStateService,
    private api: ApiService,
    private ui: UiService,
    public transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.acSubscription = this.data.ac$.subscribe({
      next: (ac: AcModel) => {
        if (ac !== null) {
          this.ac = ac;
          this.onlinetests = this.ac.onlinetests.filter((onlinetest: RunnerOnlinetestModel) => {
            if (this.isMod === true) {
              return onlinetest.type === 'before' && onlinetest.candidates.length > 0;
            } else if (this.isObserver === true) {
              return onlinetest.type === 'before' && onlinetest.candidates.length > 0 && onlinetest.with_observers === true;
            } else {
              const me = onlinetest.candidates.find(candidate => this.isMy(candidate.hashed_id));
              return onlinetest.type === 'before' && me !== undefined && me.login_url !== null;
            }
          });
          this.onlinetests.forEach((onlinetest: RunnerOnlinetestModel) => {
            // adjust candidates if one is moved to another ac
            onlinetest.candidates = onlinetest.candidates.filter((candidate: CandidateAcOnlinetestModel) => {
              return this.ac.candidates.find(actual => actual.hashed_id === candidate.hashed_id) !== undefined;
            });
            // set for observers
            if (onlinetest.with_observers === true) {
              this.showForObservers = true;
            }
          });
          this.isOpen = this.ac.status === 'planned';
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.acSubscription !== null) {
      this.acSubscription.unsubscribe();
      this.acSubscription = null;
    }
  }
  public toggleContainer(): void {
    this.isOpen = !this.isOpen;
  }

  public isMy(hashed_id: string): boolean {
    return hashed_id === this.auth.getHashedId();
  }

  public getCandidateName(candidate: any): string {
    return this.ac.candidates.find(actual => actual.id === candidate.hashed_id)?.fullname || '';
  }

  public showReloadButton(): boolean {
    return this.isMod && this.onlinetests.find(onlinetest => onlinetest.visible === true) !== undefined;
  }

  public isVisible(onlinetest: RunnerOnlinetestModel): boolean {
    return onlinetest.visible === true || this.auth.isModerator();
  }

  public openOnlinetest(onlinetest: RunnerOnlinetestModel): void {
    if (this.isMod || (this.isObserver && onlinetest.with_observers === true)) {
      this.router.navigate(['/ac/onlinetest', onlinetest.id]);
    } else {
      const candidate = onlinetest.candidates.find(candidate => this.isMy(candidate.hashed_id));
      if (candidate?.login_url) {
        window.open(candidate.login_url);
        this.setOnlinetestStarted(onlinetest);
      }
    }
  }

  public loadReports(): void {
    this.loading = true;
    this.api.getReports().subscribe({
      next: (result) => {
        const newFiles = this.ac.setNewOnlinetestReports(result.data);
        if (newFiles === 1) {
          this.ui.toastSuccess(this.transloco.translate('partials.onlinetest_nav.messages.new_one_loaded'));
        } else if (newFiles > 1) {
          this.ui.toastSuccess(this.transloco.translate('partials.onlinetest_nav.messages.new_multiple_loaded', { newFiles }));
        } else {
          this.ui.toastInfo(this.transloco.translate('partials.onlinetest_nav.messages.no_reports_found'));
        }
      },
      error: () => {
        this.ui.toastInfo(this.transloco.translate('partials.onlinetest_nav.messages.no_reports_available'));
      }
    }).add(() => {
      this.loading = false;
    });
  }

  public setExplanation(onlinetest: RunnerOnlinetestModel | null = null): void {
    this.explainedOnlinetestVisible = onlinetest !== null;
    this.explainedOnlinetest = onlinetest;
  }

  private setOnlinetestStarted(onlinetest: RunnerOnlinetestModel): void {
    this.api.setOnlinetestStarted(onlinetest.id).subscribe({
      next: (result) => {},
      error: () => {}
    });
  }

}
